// The `/benchmark` instrument's screen. Deliberately minimal (plan **D1**): the two phases in
// series, a running indication for each, and a completed state carrying both results.
//
// ## Why it is this plain
//
// This is not a screen in either mode's inventory — it is a developer measurement instrument, so
// it is deliberately absent from UI.md's screen inventory and from the `prototype/` design kit.
// It constructs no session, holds no credential and reads no store slice. Dressing it in the
// Field Journal chrome would imply it was a player surface and would couple an instrument to a
// design system it has no business depending on.
//
// ## Why the phases are injected
//
// Both phases do things a jsdom test cannot: one spawns a worker and writes OPFS, the other
// needs a GPU. Injecting them keeps this screen's own logic — the SERIES ordering, the failure
// presentation, the survive-a-failed-submission rule — testable headlessly, and it is the same
// optional-with-inert-default seam convention every other route follows.
//
// ## Why running is explicit
//
// Mounting the route must not start hammering OPFS and the GPU: the route is reachable in both
// modes and a bare mount has to be inert. The player presses Run. This also matches the spec's
// own wording — a phase's running indication is asserted "when the benchmark is STARTED".

import { useCallback, useState } from 'react';

import { submitBenchmarkReport, type SubmitOutcome } from './benchmark-http';
import { browserDeviceFacets, readDeviceDetails } from './device-details';
import {
  assembleReport,
  type BenchmarkReport,
  type DeviceDetails,
  type RenderPhaseResult,
  type SimPhaseResult,
} from './report';

/** The route path. Exported so the router and its tests share one literal. */
export const BENCHMARK_ROUTE = '/benchmark';

/** Everything the screen cannot do itself. Each has a real default in `benchmark-instrument.ts`. */
export interface BenchmarkScreenDeps {
  /** Runs phase 1 to settlement. Must resolve — a failure is a `failed` RESULT, not a throw. */
  readonly runSimulation: () => Promise<SimPhaseResult>;
  /** Runs phase 2. Only called once phase 1 has settled. */
  readonly runRender: () => Promise<RenderPhaseResult>;
  /** Submits the assembled report. */
  readonly submit?: (report: BenchmarkReport) => Promise<SubmitOutcome>;
  /** Reads the device's self-description. */
  readonly readDevice?: () => DeviceDetails;
}

type Progress =
  | { readonly kind: 'idle' }
  | { readonly kind: 'simulating' }
  | { readonly kind: 'rendering'; readonly simulation: SimPhaseResult }
  | {
      readonly kind: 'done';
      readonly report: BenchmarkReport;
      readonly submission: SubmitOutcome | { readonly kind: 'sending' };
    };

function describeSim(result: SimPhaseResult): string {
  if (result.status === 'failed') return `Simulation failed: ${result.reason}`;
  const ended =
    result.terminalCondition === 'colony_lost'
      ? 'colony lost before the horizon'
      : 'clock expired';
  return (
    `Simulation: ${result.ticksStepped} of ${result.requestedTicks} ticks (${ended}), ` +
    `peak ${result.peakBees} bees, stepping ${Math.round(result.steppingMs)} ms, ` +
    `total ${Math.round(result.totalMs)} ms`
  );
}

function describeRender(result: RenderPhaseResult): string {
  if (result.status === 'failed') return `UI test failed: ${result.reason}`;
  if (result.status === 'invalidated') {
    return `UI test invalidated: ${result.reason} — no frame figures were recorded`;
  }
  const p = result.framePacing;
  return (
    `UI test (frame pacing, not GPU cost): ${p.sampleCount} frames sampled, ` +
    `min ${p.minFps.toFixed(1)} / mean ${p.meanFps.toFixed(1)} / max ${p.maxFps.toFixed(1)} fps, ` +
    `p95 ${p.p95FrameMs.toFixed(1)} ms, p99 ${p.p99FrameMs.toFixed(1)} ms ` +
    `(${p.warmupFrameCount} warm-up frames excluded)`
  );
}

export function BenchmarkScreen({
  runSimulation,
  runRender,
  submit = submitBenchmarkReport,
  readDevice = () => readDeviceDetails(browserDeviceFacets()),
}: BenchmarkScreenDeps) {
  const [progress, setProgress] = useState<Progress>({ kind: 'idle' });

  const start = useCallback(async () => {
    setProgress({ kind: 'simulating' });
    // Awaited, not raced: running the phases concurrently would measure contention between the
    // sim worker and the renderer for the device's cores rather than measuring either subsystem.
    // The series ordering is a correctness property of the instrument, not a simplification.
    const simulation = await runSimulation();

    setProgress({ kind: 'rendering', simulation });
    const render = await runRender();

    const report = assembleReport(readDevice(), simulation, render);
    setProgress({ kind: 'done', report, submission: { kind: 'sending' } });

    // A submission failure is SHOWN, never allowed to discard the results: the device is often
    // borrowed and unreachable afterwards, so the numbers on screen have to outlive a failed POST.
    const submission = await submit(report);
    setProgress({ kind: 'done', report, submission });
  }, [runSimulation, runRender, submit, readDevice]);

  return (
    <main data-testid="benchmark-screen">
      <h1>Device benchmark</h1>

      {progress.kind === 'idle' && (
        <button type="button" data-testid="benchmark-start" onClick={() => void start()}>
          Run benchmark
        </button>
      )}

      {progress.kind === 'simulating' && (
        <p data-testid="benchmark-sim-running">Running simulation…</p>
      )}

      {progress.kind === 'rendering' && (
        <>
          <p data-testid="benchmark-sim-result">{describeSim(progress.simulation)}</p>
          <p data-testid="benchmark-render-running">Running UI test…</p>
        </>
      )}

      {progress.kind === 'done' && (
        <div data-testid="benchmark-complete">
          <p data-testid="benchmark-sim-result">{describeSim(progress.report.simulation)}</p>
          <p data-testid="benchmark-render-result">{describeRender(progress.report.render)}</p>
          {progress.submission.kind === 'sending' && <p>Submitting report…</p>}
          {progress.submission.kind === 'ok' && (
            <p data-testid="benchmark-submitted">Report submitted.</p>
          )}
          {progress.submission.kind === 'failed' && (
            <p data-testid="benchmark-submit-failed">
              Report submission failed: {progress.submission.detail}. The results above are still
              valid — record them by hand.
            </p>
          )}
        </div>
      )}
    </main>
  );
}
